// Klub / Členové (31.8.2026, zadání Ládi).
//
// Členky klubu nejsou v tabulce `clients` (počítaly by se do MRR, kapacity
// trenérů a nutričních kontrol), takže je sekce Klienti nezobrazuje. Tahle
// stránka je jediné místo, kde je vidět, kdo do klubu chodí, a jediné, kde
// jde členku přidat ručně, když platba nepřišla přes FAPI.
//
// PŘIHLÁŠENÍ STOJÍ JEN NA E-MAILU: členka ho zadá, přijde jí šestimístný
// kód. Proto je e-mail jediný povinný údaj; telefon je kontakt, nic víc.
//
// VZHLED podle stránky Klienti: jedna bílá karta, filtry s počty, hledání
// a tabulka. Na úzkém displeji se vedlejší sloupce schovají a zůstane
// členka a ovládání, aby se nemuselo posouvat do stran.
import React, { useMemo, useState } from "react";
import {
  Users, Plus, Pencil, Search, Check, Loader2, PauseCircle, PlayCircle, Ban, Mail, RefreshCw,
} from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Modal, HlavickaOkna, RozbalovaciVyber, ErrorNote, Hlaska, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../../components/PrehledDlazdice.jsx";
import { toast } from "../../components/Toast.jsx";

// 13.9.2026 (fáze 4g): stránka převedena na vzorník. Dřív měla vlastní pole,
// vlastní štítky filtrů, ručně namalované okno a textové tlačítko Obnovit
// v hlavičce. Funkce a data se nemění.

const STAV_TEXT = { aktivni: "Aktivní", pozastaveno: "Pozastaveno", ukonceno: "Ukončeno" };
const STAV_BARVA = {
  aktivni: "border-emerald-200 bg-emerald-50 text-emerald-700",
  pozastaveno: "border-amber-200 bg-amber-50 text-amber-800",
  ukonceno: "border-slate-200 bg-slate-100 text-slate-500",
};

const den = (d) => (d ? new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" }) : "—");
const denCas = (d) => (d ? new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" }) : "nikdy");

async function post(body) {
  const r = await apiFetch("/api/klub-clenove", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Akce selhala");
  return j;
}

function Okno({ titul, ikona, onZavrit, children }) {
  return (
    <Modal
      open
      onClose={onZavrit}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={ikona} nazev={titul} onClose={onZavrit} />}
    >
      {children}
    </Modal>
  );
}

/** Tlačítka u členky. Stejná v tabulce i v seznamu na telefonu. */
function AkceClenky({ clen, onUpravit, onPozvanka, onStav }) {
  const tlacitko = "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 sm:hover:bg-slate-50";
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <button onClick={() => onUpravit(clen)} title="Upravit údaje" className={tlacitko}>
        <Pencil className="h-4 w-4" />
      </button>
      <button onClick={() => onPozvanka(clen)} title="Poslat uvítací e-mail znovu" className={tlacitko}>
        <Mail className="h-4 w-4" />
      </button>
      {clen.stav === "aktivni" ? (
        <>
          <button onClick={() => onStav(clen, "pozastaveno")} title="Pozastavit členství" className={tlacitko}>
            <PauseCircle className="h-4 w-4" />
          </button>
          <button onClick={() => onStav(clen, "ukonceno")} title="Ukončit členství" className={tlacitko}>
            <Ban className="h-4 w-4" />
          </button>
        </>
      ) : (
        <button onClick={() => onStav(clen, "aktivni")} title="Obnovit členství" className={tlacitko}>
          <PlayCircle className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** Členka na telefonu. Tabulka se sem nevejde, akce z ní vyjížděly mimo obrazovku. */
function RadekClenky({ clen, onUpravit, onPozvanka, onStav }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
          {clen.jmeno || "Bez jména"}
          {clen.prezdivka && <span className="ml-1.5 font-normal text-slate-400">({clen.prezdivka})</span>}
        </span>
      </div>
      <a href={`mailto:${clen.email}`} className="mt-0.5 block truncate text-sm text-slate-500">{clen.email}</a>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STAV_BARVA[clen.stav]}`}>
          {STAV_TEXT[clen.stav] || clen.stav}
        </span>
        <Pilulka>{clen.zdroj}</Pilulka>
        {!clen.profilHotovy && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
            bez profilu
          </span>
        )}
        {clen.kalorUroven && <Pilulka>{clen.kalorUroven} kcal</Pilulka>}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {clen.telefon || "bez telefonu"}, od {den(clen.zacatek)}
      </div>
      <div className="mt-2 flex justify-end">
        <AkceClenky clen={clen} onUpravit={onUpravit} onPozvanka={onPozvanka} onStav={onStav} />
      </div>
    </div>
  );
}

function Popisek({ children, doplnek }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {children}{doplnek && <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">{doplnek}</span>}
    </label>
  );
}

function Formular({ clen, novy, onUlozit, onZrusit, ukladam }) {
  const [c, setC] = useState(() => ({
    email: clen.email || "", jmeno: clen.jmeno || "", telefon: clen.telefon || "",
    zacatek: clen.zacatek ? String(clen.zacatek).slice(0, 10) : new Date().toISOString().slice(0, 10),
    pozvanka: true,
  }));
  const zmen = (k, v) => setC((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4">
      <div>
        <Popisek doplnek="přihlašuje se jím, musí sedět">E-mail</Popisek>
        <input className="field" type="email" value={c.email} maxLength={120}
          onChange={(e) => zmen("email", e.target.value)} placeholder="jarmila@email.cz" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Popisek>Jméno</Popisek>
          <input className="field" value={c.jmeno} maxLength={80}
            onChange={(e) => zmen("jmeno", e.target.value)} placeholder="Jarmila Nováková" />
        </div>
        <div>
          <Popisek doplnek="jen kontakt">Telefon</Popisek>
          <input className="field" value={c.telefon} maxLength={20}
            onChange={(e) => zmen("telefon", e.target.value)} placeholder="+420 777 123 456" />
        </div>
      </div>
      <div>
        <Popisek doplnek="od kdy členství běží">Začátek</Popisek>
        <input className="field" type="date" value={c.zacatek}
          onChange={(e) => zmen("zacatek", e.target.value)} />
      </div>
      {novy && (
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-plum-600"
            checked={c.pozvanka} onChange={(e) => zmen("pozvanka", e.target.checked)} />
          <span className="text-sm">
            <strong className="block text-slate-800">Poslat uvítací e-mail</strong>
            <span className="text-slate-500">
              Z info@zateckal.cz jí přijde pozvánka s tím, co v klubu najde a jak se přihlásí.
              Členky z FAPI ji nedostávají, aby po platbě nechodila dvě uvítání za sebou.
            </span>
          </span>
        </label>
      )}

      <p className="text-sm text-slate-500">
        Členka se do klubu přihlásí sama: na klub.fitway40.cz zadá tenhle e-mail a přijde
        jí šestimístný kód. Žádné heslo se nikde nezakládá.
      </p>
      <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <button type="button" disabled={ukladam} onClick={() => onUlozit(c)}
          className="btn btn-primary">
          {ukladam ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Uložit
        </button>
        <button type="button" onClick={onZrusit}
          className="btn btn-secondary">
          Zrušit
        </button>
      </div>
    </div>
  );
}

export default function KlubClenove() {
  // bezKese: po přidání členky musí být seznam hned aktuální.
  const { data, loading, error, refetch } = useApiData("/api/klub-clenove", { bezKese: true });
  const [filtr, setFiltr] = useState("aktivni");
  const [hledani, setHledani] = useState("");
  const [edit, setEdit] = useState(null);       // { clen, novy }
  const [ukladam, setUkladam] = useState(false);
  const uzka = useUzkaObrazovka();

  const clenove = data?.clenove || [];
  const pocty = data?.pocty || {};

  const videt = useMemo(() => {
    const h = hledani.trim().toLowerCase();
    return clenove.filter((c) => (filtr === "vse" || c.stav === filtr)
      && (!h || c.email.toLowerCase().includes(h)
        || (c.jmeno || "").toLowerCase().includes(h)
        || (c.telefon || "").includes(h)));
  }, [clenove, filtr, hledani]);

  async function ulozit(c) {
    setUkladam(true);
    try {
      if (edit.novy) {
        const r = await post({ action: "pridat", ...c });
        // Pozvánka je best effort, členka se založí i tak. Když neodešla,
        // musí to být vidět, jinak by Láďa čekal, že se členka přihlásí.
        if (r.pozvanka === "selhalo") {
          toast("Členka přidána, ale pozvánku se nepodařilo odeslat. Zkuste ji poslat znovu z tabulky.", "error");
        } else if (r.pozvanka === "odeslano") {
          toast("Členka přidána a pozvánka odeslána.", "success");
        } else {
          toast("Členka přidána, může se přihlásit.", "success");
        }
      } else {
        await post({ action: "upravit", id: edit.clen.id, ...c });
        toast("Údaje uloženy.", "success");
      }
      setEdit(null);
      refetch();
    } catch (e) { toast(e.message, "error"); }
    finally { setUkladam(false); }
  }

  async function poslatPozvanku(c) {
    try {
      await post({ action: "pozvanka", id: c.id });
      toast(`Pozvánka odeslána na ${c.email}.`, "success");
    } catch (e) { toast(e.message, "error"); }
  }

  async function zmenStav(c, stav) {
    try {
      await post({ action: "stav", id: c.id, stav });
      toast(stav === "aktivni" ? "Členství běží dál."
        : stav === "pozastaveno" ? "Členství pozastaveno." : "Členství ukončeno.", "success");
      refetch();
    } catch (e) { toast(e.message, "error"); }
  }

  const filtry = [
    { id: "aktivni", label: "Aktivní", pocet: pocty.aktivni || 0 },
    { id: "pozastaveno", label: "Pozastavené", pocet: pocty.pozastaveno || 0 },
    { id: "ukonceno", label: "Ukončené", pocet: pocty.ukonceno || 0 },
    { id: "vse", label: "Vše", pocet: pocty.celkem || 0 },
  ];

  return (
    <Stranka>
      <GradientHeader
        title="Členové klubu"
        icon={Users}
        loading={loading}
        napoveda="Členky klubu nejsou v Klientech, protože by se počítaly do MRR, kapacity trenérů a nutričních kontrol. Tohle je jediné místo, kde je vidět, kdo do klubu chodí, a kde jde členku přidat ručně, když platba nepřišla přes FAPI. Přihlašuje se jen e-mailem, proto je jediný povinný."
        akce={[
          { klic: "pridat", ikona: Plus, popis: "Přidat členku", onClick: () => setEdit({ clen: {}, novy: true }) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading },
        ]}
      />

      {error && <ErrorNote onRetry={refetch}>Členy se nepodařilo načíst: {String(error)}</ErrorNote>}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Users}
          nazev="Členové"
          podtitulek={`${videt.length} ${videt.length === 1 ? "zobrazená" : videt.length < 5 ? "zobrazené" : "zobrazených"}`}
          odznak={`${pocty.aktivni || 0} aktivních`}
        />

        <div className="space-y-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
          <RozbalovaciVyber
            stitek="Stav"
            aria-label="Filtr podle stavu členství"
            value={filtr}
            onChange={setFiltr}
            items={filtry.map((f) => ({ key: f.id, label: `${f.label} (${f.pocet})` }))}
          />
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="field pl-9" placeholder="Hledat podle e-mailu, jména nebo telefonu"
              value={hledani} onChange={(e) => setHledani(e.target.value)} />
          </div>
        </div>

        {pocty.bezProfilu > 0 && (
          <div className="border-b border-slate-200 p-3 sm:p-4">
            <Hlaska ton="pozor" title={`${pocty.bezProfilu} aktivních členek nemá vyplněný profil`}>
              Dokud ho nevyplní, klub jim nepřiřadí kalorickou úroveň a nevidí jídelníček.
            </Hlaska>
          </div>
        )}

        {loading && !data && <div className="p-3 sm:p-4"><SkeletonRows rows={6} /></div>}

        {data && (videt.length === 0 ? (
          <EmptyState icon={Users} title="Žádná členka neodpovídá filtru" hint="Zkus jiný stav nebo vymaž hledání." />
        ) : uzka ? (
          /* 13.9.2026 (fáze 4g): na telefonu seznam řádků. Tabulka se sem
             nevešla, akce i sloupec Akce vyjížděly mimo obrazovku a e-mail
             se lámal uprostřed slova. Na počítači zůstává tabulka. */
          <div className="divide-y divide-slate-100">
            {videt.map((c) => (
              <RadekClenky
                key={c.id}
                clen={c}
                onUpravit={(x) => setEdit({ clen: x, novy: false })}
                onPozvanka={poslatPozvanku}
                onStav={zmenStav}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Členka</th>
                  <th className="px-2 py-3 text-left font-semibold hidden md:table-cell">Telefon</th>
                  <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Členství od</th>
                  <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Naposledy</th>
                  <th className="px-2 py-3 text-left font-semibold hidden md:table-cell">Úroveň</th>
                  <th className="px-4 py-3 text-right font-semibold sm:px-5">Akce</th>
                </tr>
              </thead>
              <tbody>
                {videt.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-3 sm:px-5">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800">
                          {c.jmeno || "Bez jména"}
                          {c.prezdivka && <span className="ml-1.5 font-normal text-slate-400">({c.prezdivka})</span>}
                        </div>
                        <a href={`mailto:${c.email}`} className="text-slate-500 hover:text-plum-600 break-all">{c.email}</a>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${STAV_BARVA[c.stav]}`}>
                            {STAV_TEXT[c.stav] || c.stav}
                          </span>
                          <Pilulka>{c.zdroj}</Pilulka>
                          {!c.profilHotovy && (
                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                              bez profilu
                            </span>
                          )}
                          {c.konec && (
                            <Pilulka>do {den(c.konec)}</Pilulka>
                          )}
                        </div>
                        <div className="mt-1.5 text-slate-500 md:hidden">
                          {c.telefon || "bez telefonu"}, od {den(c.zacatek)}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 hidden md:table-cell text-slate-600">
                      {c.telefon ? <a href={`tel:${c.telefon.replace(/\s/g, "")}`} className="hover:text-plum-600">{c.telefon}</a> : "—"}
                    </td>
                    <td className="px-2 py-3 hidden lg:table-cell text-slate-600">{den(c.zacatek)}</td>
                    {/* 25.10.2026: kdy naposledy klub otevřela (Aktivita členek). Přihlášení
                        kódem vydrží 90 dní, samo o používání nic neříká; zůstává
                        jako záloha u členek bez záznamu aktivity. */}
                    <td className="px-2 py-3 hidden lg:table-cell text-slate-600">{denCas(c.posledneAktivni || c.poslednePrihlasena)}</td>
                    <td className="px-2 py-3 hidden md:table-cell text-slate-600">
                      {c.kalorUroven ? `${c.kalorUroven} kcal` : "—"}
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex justify-end">
                        <AkceClenky
                          clen={c}
                          onUpravit={(x) => setEdit({ clen: x, novy: false })}
                          onPozvanka={poslatPozvanku}
                          onStav={zmenStav}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </KartaPrehledu>

      {edit && (
        <Okno
          titul={edit.novy ? "Přidat členku klubu" : "Upravit údaje členky"}
          ikona={edit.novy ? Plus : Pencil}
          onZavrit={() => setEdit(null)}
        >
          <Formular clen={edit.clen} novy={edit.novy} ukladam={ukladam} onUlozit={ulozit} onZrusit={() => setEdit(null)} />
        </Okno>
      )}
    </Stranka>
  );
}
